import type { ComparisonFilters } from "../../../shared/contracts/product-pages.js";
import {
  rivalInsightFilters,
  type RivalInsight,
  type RivalInsightContext,
} from "../../../shared/presentation/rival-insight.js";
import { comparisonQuery } from "./comparison-query.js";

export const insightQueryKeys = [
  "insight",
  "hole",
  "basis",
  "courseId",
  "teePair",
  "weather",
  "transport",
  "environment",
  "format",
];

export interface InsightSelection {
  context: RivalInsightContext;
  filters: ComparisonFilters;
  topic: "overview" | "holes" | "conditions";
}

export function insightQuery(finding: RivalInsight): string {
  const query = new URLSearchParams(
    comparisonQuery(rivalInsightFilters(finding)),
  );
  const { context } = finding;
  query.set(
    "insight",
    context.kind === "phase"
      ? context.phase
      : context.kind === "condition"
        ? context.value
        : "hole",
  );
  if (context.kind === "hole") query.set("hole", String(context.holeNumber));
  return query.toString();
}

export function readInsightSelection(search: string): InsightSelection | null {
  const query = new URLSearchParams(search);
  const subject = query.get("insight");
  const filters: ComparisonFilters = {
    basis: query.get("basis") === "net" ? "net" : "gross",
  };
  if (query.get("period") === "all") filters.period = "all";
  const year = Number(query.get("year"));
  if (
    query.get("period") === "year" &&
    Number.isInteger(year) &&
    year >= 1900 &&
    year <= 9999
  ) {
    filters.period = "year";
    filters.year = year;
  }
  if (query.get("ledger") === "main" || query.get("ledger") === "simulator")
    filters.ledger = query.get("ledger") as "main" | "simulator";
  const environment = query.get("environment");
  if (
    environment === "outdoor" ||
    environment === "simulator" ||
    environment === "unknown"
  )
    filters.environment = environment;
  if (
    subject === "opening" ||
    subject === "front9" ||
    subject === "back9" ||
    subject === "closing3"
  )
    return {
      context: { kind: "phase", phase: subject },
      filters,
      topic: "overview",
    };
  if (
    subject === "weather-rain" ||
    subject === "both-cart" ||
    subject === "both-walking"
  ) {
    const context: RivalInsightContext = { kind: "condition", value: subject };
    return {
      context,
      filters: rivalInsightFilters({ context, filters }),
      topic: "conditions",
    };
  }
  const courseId = query.get("courseId"),
    teePair = query.get("teePair");
  const holeNumber = Number(query.get("hole"));
  if (
    subject === "hole" &&
    courseId &&
    teePair &&
    Number.isInteger(holeNumber) &&
    holeNumber >= 1 &&
    holeNumber <= 18
  ) {
    const context = { kind: "hole" as const, courseId, teePair, holeNumber };
    return {
      context,
      filters: rivalInsightFilters({ context, filters }),
      topic: "holes",
    };
  }
  return null;
}
